import type { DropoffInformationType } from '@/features/map/types/dropoffInformationType';
import type { PickupDropoffInformationType } from '@/features/map/types/pickupDropoffInformationType';
import type { PickupInformationType } from '@/features/map/types/pickupInformationType';
import type { WaypointType } from '@/features/map/types/waypointType';

const sortReservations = (
  a: { latestEstimatedDatetime: string; pickupDropoffKbn: number; waypointSequence: string },
  b: typeof a,
) => {
  const datetimeA = new Date(a.latestEstimatedDatetime).getTime();
  const datetimeB = new Date(b.latestEstimatedDatetime).getTime();
  if (datetimeA !== datetimeB) return datetimeA - datetimeB;
  if (a.pickupDropoffKbn !== b.pickupDropoffKbn) return b.pickupDropoffKbn - a.pickupDropoffKbn;

  return Number(a.waypointSequence) - Number(b.waypointSequence);
};

const hasUniqueSequence = (sequences: (string | null | undefined)[]): boolean =>
  sequences.some((sequence) => {
    const isInvalid = sequence === '' || sequence === null || sequence === undefined;
    const isUnique = sequences.indexOf(sequence) === sequences.lastIndexOf(sequence);

    return isInvalid || isUnique;
  });

export const calculatePickupAreaFlg = (pickupInformation: PickupInformationType[]): 1 | 2 => {
  const hasUnique = pickupInformation.some((info) =>
    hasUniqueSequence(info.reservationIdList.map((res) => res.waypointSequence)),
  );

  return hasUnique ? 2 : 1;
};

export const calculateDropoffAreaFlg = (dropoffInformation: DropoffInformationType[]): 1 | 2 => {
  const hasUnique = dropoffInformation.some((info) =>
    hasUniqueSequence(info.reservationIdList.map((res) => res.waypointSequence)),
  );

  return hasUnique ? 2 : 1;
};

export const calculatePickupDropoffAreaFlg = (pickupDropoffInformation: PickupDropoffInformationType[]): 1 | 2 => {
  const hasUnique = pickupDropoffInformation.some((info) => {
    const all = [
      ...info.pickupReservationIdList.map((res) => res.waypointSequence),
      ...info.dropoffReservationIdList.map((res) => res.waypointSequence),
    ];

    return hasUniqueSequence(all);
  });

  return hasUnique ? 2 : 1;
};

export const transformAndSortPickupData = (data: PickupInformationType[]): WaypointType[] => {
  const waypoints: WaypointType[] = data.map((item) => ({
    waypointId: item.pickupWaypointId,
    waypointNumber: item.pickupWaypointNumber,
    waypointName: item.pickupWaypointName,
    waypointPosition: item.pickupWaypointPosition,
    reservationIdList: item.reservationIdList.map((res) => ({
      reservationId: res.reservationId,
      userName: res.userName,
      latestEstimatedDatetime: res.latestEstimatedDatetime,
      pickupDropoffKbn: 1,
      waypointSequence: res.waypointSequence,
    })),
  }));

  return waypoints.map((waypoint) => ({
    ...waypoint,
    reservationIdList: [...waypoint.reservationIdList].sort(sortReservations),
  }));
};

export const transformAndSortDropoffData = (data: DropoffInformationType[]): WaypointType[] => {
  const waypoints: WaypointType[] = data.map((item) => ({
    waypointId: item.dropoffWaypointId,
    waypointNumber: item.dropoffWaypointNumber,
    waypointName: item.dropoffWaypointName,
    waypointPosition: item.dropoffWaypointPosition,
    reservationIdList: item.reservationIdList.map((res) => ({
      reservationId: res.reservationId,
      userName: res.userName,
      latestEstimatedDatetime: res.latestEstimatedDatetime,
      pickupDropoffKbn: 2,
      waypointSequence: res.waypointSequence,
    })),
  }));

  return waypoints.map((waypoint) => ({
    ...waypoint,
    reservationIdList: [...waypoint.reservationIdList].sort(sortReservations),
  }));
};

export const transformAndSortPickupDropoffData = (data: PickupDropoffInformationType[]): WaypointType[] => {
  const waypointMap: Record<number, WaypointType> = {};
  data.forEach((item) => {
    const reservations = [
      ...item.pickupReservationIdList.map((res) => ({
        reservationId: res.reservationId,
        userName: res.userName,
        latestEstimatedDatetime: res.latestEstimatedDatetime,
        pickupDropoffKbn: 1,
        waypointSequence: res.waypointSequence,
      })),
      ...item.dropoffReservationIdList.map((res) => ({
        reservationId: res.reservationId,
        userName: res.userName,
        latestEstimatedDatetime: res.latestEstimatedDatetime,
        pickupDropoffKbn: 2,
        waypointSequence: res.waypointSequence,
      })),
    ];
    if (waypointMap[item.pickupWaypointId]) {
      waypointMap[item.pickupWaypointId].reservationIdList.push(...reservations);
    } else {
      waypointMap[item.pickupWaypointId] = {
        waypointId: item.pickupWaypointId,
        waypointNumber: item.pickupWaypointNumber,
        waypointName: item.pickupWaypointName,
        waypointPosition: item.pickupWaypointPosition,
        reservationIdList: reservations,
      };
    }
  });

  return Object.values(waypointMap).map((waypoint) => ({
    ...waypoint,
    reservationIdList: [...waypoint.reservationIdList].sort(sortReservations),
  }));
};
